import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, can } from '../../lib/api';
import type { Site } from '../../lib/types';
import { Badge, Banner, Button, Card, input } from '../../lib/ui';
import { categoryLabel } from '../../lib/labels';
import { Hilfe } from '../help/Help';
import { fmtDateTime, t, tn } from '../../lib/i18n';
import { ReviewQueue, type Suggestion } from './ReviewQueue';

/**
 * The scan inbox (Doc 04 §7.1).
 *
 * Leads with what fired before consent, because that is the compliance gap and everything
 * else is inventory. A suggestion is never applied on its own — the scanner proposes, a
 * person decides, and a wrong automatic categorisation is a legal risk.
 *
 * Its own area rather than a card below the rule table, because a run takes minutes and
 * people watch it: progress at the bottom of a long screen is progress nobody sees.
 */
interface Finding {
  vendor_key: string;
  name: string;
  category_slug: string;
  confidence: number;
  fired_pre_consent: boolean;
  domains?: string[];
  cookies?: string[];
  requests?: string[];
  pages?: string[];
}
interface Scan {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  start_url: string;
  pages_done: number;
  requests_seen: number;
  cookies_seen: number;
  suppressed: number;
  findings: Finding[];
  error: string | null;
  finished_at: string | null;
  /** What this run was started with. Optional because older records predate the field. */
  max_pages?: number;
}

/**
 * The page limit assumed when nobody has said otherwise.
 *
 * Never sent — an empty field means "whatever the server defaults to", and putting a
 * number in the body would freeze that default at whatever it happened to be the day this
 * was written. It exists only as a denominator, so a bar can be drawn before the first
 * poll comes back with the run's real limit.
 */
const DEFAULT_MAX_PAGES = 10;

export function ScanScreen({ site }: { site: Site }) {
  const qc = useQueryClient();
  /**
   * An accepted suggestion becomes a vendor, and the vendor list lives on another tab.
   *
   * It used to be a callback into the screen that owned both, which is gone now that the
   * scan is its own area. Dropping the keys instead of the prop keeps the two tabs from
   * disagreeing about who is disclosed — a stale list there is a missing recipient in the
   * dialog, not a cosmetic problem.
   */
  const vendorsChanged = (): void => {
    void qc.invalidateQueries({ queryKey: ['vendors', site.id] });
    void qc.invalidateQueries({ queryKey: ['effective', site.id] });
  };
  const base = `/v1/sites/${site.id}`;
  const [url, setUrl] = useState(`https://${site.primary_domain}/`);
  // A string, not a number: the empty field is a state of its own ("server decides") and
  // `useState(0)` cannot tell that apart from someone asking for zero pages.
  const [maxPages, setMaxPages] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);

  const scan = useQuery({
    queryKey: ['scan', site.id],
    queryFn: () => api<Scan | null>(`${base}/scan`),
    // Only while something is in flight: a finished scan does not change on its own, and
    // polling it forever would be a request per second per open tab.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'queued' || s === 'running' ? 2000 : false;
    },
  });
  const suggestions = useQuery({
    queryKey: ['suggestions', site.id],
    queryFn: () => api<{ data: Suggestion[] }>(`${base}/scan/suggestions`),
  });

  const start = useMutation({
    mutationFn: () => api<Scan>(`${base}/scan`, {
      method: 'POST',
      // Omitted rather than sent as a number the client invented: the server owns the
      // default and the bounds, and a value it never received is a value it cannot reject.
      body: { start_url: url, ...(maxPages.trim() !== '' && { max_pages: Number(maxPages) }) },
    }),
    onSuccess: () => { setProblem(null); void qc.invalidateQueries({ queryKey: ['scan', site.id] }); },
    onError: (e) => setProblem(scanProblem(e)),
  });
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: 'accept' | 'reject' }) =>
      api(`${base}/scan/suggestions/${v.id}`, { method: 'POST', body: { decision: v.decision } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suggestions', site.id] });
      vendorsChanged();
    },
  });

  const s = scan.data;
  const running = s?.status === 'queued' || s?.status === 'running';
  const violations = (s?.findings ?? []).filter((f) => f.fired_pre_consent);
  const rest = (s?.findings ?? []).filter((f) => !f.fired_pre_consent);
  const open = suggestions.data?.data ?? [];

  return (
    <div className="space-y-4">
      <Card title={t('Website scannen')} actions={<Hilfe to="scan" />}>
        <p className="mb-3 text-xs text-[var(--color-muted)]">
          {t('Lädt die Seite zweimal: einmal ohne Einwilligung, einmal mit. Die erste Runde zeigt, was trotz Banner feuert. Anfragen an Anbieter werden dabei beobachtet und im Browser gestoppt — der Scan landet nicht als Besuch in Ihrer Statistik.')}
        </p>

        {can('site:update') && <WeeklyToggle site={site} />}

        {can('scan:run') && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              <input className={`${input} min-w-[16rem] flex-1`} value={url} onChange={(e) => setUrl(e.target.value)}
                aria-label={t('Start-URL')} />
              {/* `min` is a typo guard, not the rule: the server owns the bounds, and a
                  number field is trivially bypassed anyway. */}
              <input className={`${input} w-24 shrink-0 tabular`} type="number" min={1} inputMode="numeric"
                value={maxPages} onChange={(e) => setMaxPages(e.target.value)}
                placeholder={String(DEFAULT_MAX_PAGES)} aria-label={t('Seiten höchstens')} />
              <Button variant="secondary" disabled={running || start.isPending}
                onClick={() => start.mutate()}>
                {running ? t('läuft …') : t('Scan starten')}
              </Button>
            </div>
            <p className="mt-1.5 text-xs text-[var(--color-muted)]">
              {t('Die Zahl begrenzt, wie viele Seiten eine Runde besucht. Leer lassen für die Voreinstellung.')}
            </p>
          </div>
        )}
        {problem && <Banner kind="error">{problem}</Banner>}

        {!s && <p className="text-sm text-[var(--color-muted)]">{t('Noch kein Scan gelaufen.')}</p>}

        {s && running && <Progress scan={s} assumed={Number(maxPages) || DEFAULT_MAX_PAGES} />}

        {s?.status === 'failed' && (
          <Banner kind="error">{t('Scan fehlgeschlagen: {error}', { error: s.error ?? '' })}</Banner>
        )}

        {s?.status === 'done' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--color-muted)]">
              <span>{tn('{n} Seite geprüft', '{n} Seiten geprüft', s.pages_done)}</span>
              <span>{t('{n} Anfragen beobachtet', { n: s.requests_seen })}</span>
              <span>{t('{n} Cookies', { n: s.cookies_seen })}</span>
              <span>{t('{n} gestoppt', { n: s.suppressed })}</span>
              {s.finished_at && (
                <span className="tabular">{fmtDateTime(s.finished_at)}</span>
              )}
            </div>

            {violations.length > 0 ? (
              <div>
                <div className="eyebrow mb-2 text-[var(--color-danger)]">
                  {t('Vor der Einwilligung geladen ({n})', { n: violations.length })}
                </div>
                <ul className="space-y-1.5">
                  {violations.map((f) => <FindingRow key={f.vendor_key} f={f} alarming />)}
                </ul>
              </div>
            ) : (
              <Banner kind="ok">
                {t('Vor der Einwilligung wurde kein Drittanbieter geladen.')}
              </Banner>
            )}

            {rest.length > 0 && (
              <div>
                <div className="eyebrow mb-2">{t('Nach der Einwilligung ({n})', { n: rest.length })}</div>
                <ul className="space-y-1.5">
                  {rest.map((f) => <FindingRow key={f.vendor_key} f={f} />)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      {reviewing && (
        <ReviewQueue
          items={open}
          base={base}
          onDone={() => { void qc.invalidateQueries({ queryKey: ['suggestions', site.id] }); vendorsChanged(); }}
          onClose={() => setReviewing(false)}
        />
      )}

      {open.length > 0 && (
        <Card
          title={t('Vorschläge ({n})', { n: open.length })}
          actions={can('vendor:write') && (
            <Button variant="secondary" onClick={() => setReviewing(true)}>
              {t('Nacheinander durchgehen')}
            </Button>
          )}
        >
          <p className="mb-3 text-xs text-[var(--color-muted)]">
            {t('Der Scanner entscheidet nichts. Übernommene Anbieter erscheinen im Auswahl-Dialog. Solange ein Vorschlag offen ist, fehlt der Anbieter dort als Empfänger.')}
          </p>
          <ul className="divide-y divide-[var(--color-line)]">
            {open.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2 text-[13px]">
                    <span className="font-medium">{v.name}</span>
                    <Badge>{categoryLabel(v.category_slug)}</Badge>
                  </div>
                  <div className="truncate text-xs text-[var(--color-muted)]">
                    {[...(v.domains ?? []), ...(v.cookies ?? [])].join(', ') || '—'}
                  </div>
                </div>
                {can('vendor:write') && (
                  <div className="flex shrink-0 gap-3 text-xs">
                    <button className="text-[var(--color-brand)] hover:underline"
                      onClick={() => decide.mutate({ id: v.id, decision: 'accept' })}>
                      {t('Übernehmen')}
                    </button>
                    <button className="text-[var(--color-muted)] hover:underline"
                      onClick={() => decide.mutate({ id: v.id, decision: 'reject' })}>
                      {t('Verwerfen')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/**
 * How far along the run is.
 *
 * The old line said "N Seiten geprüft" and nothing else, so a scan waiting in the queue
 * looked exactly like one that had stalled on page one. Pages against the run's own limit
 * is the only real fraction on offer, and it is an upper bound rather than a total — a
 * crawl that runs out of links finishes below 100%, which is why the label says
 * "höchstens" instead of claiming to know how many pages there are.
 *
 * The three counters below the bar move on every poll even while the page count stands
 * still, which is what distinguishes a slow page from a dead run. `assumed` covers the
 * gap after a reload, when the number that was sent is gone and the record has not (yet)
 * reported one back.
 */
function Progress({ scan, assumed }: { scan: Scan; assumed: number }) {
  const limit = Math.max(1, scan.max_pages ?? assumed);
  // Capped, not scaled: a run that overshoots an assumed limit should sit at the end of
  // the bar rather than redraw the scale and appear to go backwards.
  const done = Math.min(scan.pages_done, limit);
  const share = (100 * done) / limit;

  return (
    <div className="rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-[13px]">
        <span>{scan.status === 'queued' ? t('In der Warteschlange …') : t('Scan läuft …')}</span>
        <span className="tabular text-xs text-[var(--color-muted)]">
          {tn('{n} von höchstens {max} Seite', '{n} von höchstens {max} Seiten', done, { max: limit })}
        </span>
      </div>
      <div
        className="mt-2 h-1.5 overflow-hidden rounded-[1px] bg-[var(--color-sunken)]"
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-label={t('Fortschritt des Scans')}
      >
        {/* Zero pages draws no bar at all. An empty track next to "In der Warteschlange"
            is honest; a full one, or a token sliver, would read as "nearly done". */}
        <div className="h-full bg-[var(--color-brand)] transition-[width] duration-500"
          style={{ width: `${share}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--color-muted)]">
        <span>{t('{n} Anfragen beobachtet', { n: scan.requests_seen })}</span>
        <span>{t('{n} Cookies', { n: scan.cookies_seen })}</span>
        <span>{t('{n} gestoppt', { n: scan.suppressed })}</span>
      </div>
    </div>
  );
}

function FindingRow({ f, alarming }: { f: Finding; alarming?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <li className={alarming ? 'rounded-[3px] border-l-2 border-[var(--color-danger)] bg-[var(--color-danger)]/6 px-2 py-1.5' : 'px-2 py-1'}>
      <button className="flex w-full items-baseline justify-between gap-2 text-left"
        onClick={() => setOpen(!open)}>
        <span className="flex items-baseline gap-2 text-[13px]">
          <span className="font-medium">{f.name}</span>
          <Badge>{categoryLabel(f.category_slug)}</Badge>
        </span>
        <span className="shrink-0 text-xs text-[var(--color-muted)]">
          {/* Confidence 0 is not "harmless", it is "nobody knows what this is" — which is
              the row most worth opening. */}
          {f.confidence === 0 ? t('nicht erkannt') : t('erkannt')}
        </span>
      </button>
      {open && (
        <div className="mt-1 space-y-0.5 pl-1 font-mono text-[11px] text-[var(--color-muted)]">
          {(f.requests ?? []).map((r) => <div key={r} className="truncate">{r}</div>)}
          {(f.cookies ?? []).length > 0 && <div>{t('Cookies: {list}', { list: f.cookies!.join(', ') })}</div>}
          {(f.pages ?? []).length > 0 && <div>{t('Seiten: {list}', { list: f.pages!.join(', ') })}</div>}
        </div>
      )}
    </li>
  );
}

function scanProblem(e: unknown): string {
  if (!(e instanceof ApiError)) return t('Scan konnte nicht gestartet werden.');
  if (e.status === 409) return t('Für diese Website läuft bereits ein Scan.');
  // By field rather than by code: the server owns the bounds on the page limit and may
  // name its refusal whatever it likes, and "Scan konnte nicht gestartet werden" next to a
  // field the user just typed in is the message that sends people to support.
  if ('max_pages' in e.fieldErrors) {
    return t('Diese Seitenzahl akzeptiert der Server nicht. Lassen Sie das Feld leer für die Voreinstellung.');
  }
  const codes = (e.problem.errors ?? []).map((x) => x.code);
  if (codes.includes('not_a_verified_domain')) {
    return t('Diese Adresse gehört nicht zu den bestätigten Domains dieser Website.');
  }
  if (codes.includes('scheme')) return t('Nur http:// und https:// sind möglich.');
  return t('Scan konnte nicht gestartet werden.');
}


/**
 * The scan on a schedule.
 *
 * A scanner that only runs when someone presses a button finds what changed on the day
 * they happened to think of it. Sites gain tags between visits — a marketing team adds a
 * pixel, an agency ships a container — and noticing that is the whole job.
 */
function WeeklyToggle({ site }: { site: Site }) {
  const qc = useQueryClient();
  const on = site.scan_weekly === true;
  const set = useMutation({
    mutationFn: (v: boolean) => api(`/v1/sites/${site.id}`, { method: 'PATCH', body: { scan_weekly: v } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site', site.id] }),
  });

  return (
    <label className="mb-4 flex items-start gap-2 rounded-[3px] border border-[var(--color-line)]
                      bg-[var(--color-paper)] p-3 text-[13px]">
      <input type="checkbox" className="mt-0.5" checked={on} disabled={set.isPending}
        onChange={(e) => set.mutate(e.target.checked)} />
      <span>
        {t('Wöchentlich automatisch scannen')}
        <span className="block text-xs text-[var(--color-muted)]">
          {site.scan_last_at
            ? `${t('Zuletzt {when}.', { when: fmtDateTime(site.scan_last_at) })} `
            : ''}
          {/* Only on a finding: a weekly "nothing changed" is a mail people filter away,
              and then they miss the one that matters. */}
          {t('Eine Mail geht nur raus, wenn neue Anbieter auftauchen — an Inhaber und Administratoren dieses Kontos.')}
        </span>
      </span>
    </label>
  );
}
